import { Banknote, Clock, HeartPulse, IterationCcw, PiggyBank } from 'lucide-vue-next'
import ColumnLink from 'pejer/src/components/Table/core/ColumnLink.vue'

import { h } from 'vue'
import { Groups, JSSchema } from '.'
import { MoneyFormatter } from './Global'
import { ColorPalette, textWithIconCell } from './helpers'

export namespace Refunds {
  export interface Full extends ListItem {}

  export interface ListItem {
    id: number
    platformID: string
    environment: string
    payoutReason: string
    paymentReferenceID: string
    paidWithKID: string
    fromBankAccountNumber: string
    toBankAccountNumber: string
    amount: number
    shortDescription: string
    sftpPayoutStatus:
      | 'Pain001IsUploadedToSFTP'
      | 'Pain002ReceiptIsReceivedFromSFTP'
      | 'PayoutIsRejected'
      | 'Pain002PayoutIsAccepted'
      | 'PayoutIsSuccessful'

    insertTime: string
    updateTime: string
    isAccepted: string

    retryCount: number
    previouslyRetriedAt: string | null
  }

  export type Statistics = {
    firstTryRefundsSucceeded: number
    firstTryRefundsSucceededSum: number
    firstTryRefundsFailed: number
    firstTryRefundsFailedSum: number

    currentRefundsSucceeded: number
    currentRefundsSucceededSum: number
    currentRefundsFailed: number
    currentRefundsFailedSum: number
    currentAwaitingBankConfirmation: number
    currentAwaitingBankConfirmationSum: number

    refundsAccepted: number
    refundsNotAccepted: number
    refundsNotAcceptedSum: number

    totalSum: number
  }
}
export const RefundGroups = {
  Refund: {
    icon: IterationCcw,
    color: ColorPalette.red,
    isMainGroup: true,
    mainColumn: 'payoutReason',
  },
  BankDetails: {
    icon: PiggyBank,
    color: ColorPalette.blue,
    mainColumn: 'toBankAccountNumber',
  },
  'Is Accepted': {
    icon: HeartPulse,
    color: ColorPalette.green,
    mainColumn: 'isAccepted',
  },
  Time: {
    icon: Clock,
    color: ColorPalette.black,
    mainColumn: 'insertTime',
  },
} satisfies Groups<Refunds.ListItem>

export const RefundsListSchema: JSSchema<Refunds.ListItem, typeof RefundGroups> = {
  name: 'Refunds',
  title: 'Refunds',
  groups: RefundGroups,
  fields: {
    id: {
      type: 'number',
      humanReadable: 'ID',
      meta: {
        group: 'Refund',
      },
      isDefaultSortColumn: true,
    },
    platformID: {
      type: 'string',
      humanReadable: 'Platform ID',
      meta: {
        group: 'Refund',
      },
    },
    environment: {
      type: 'string',
      humanReadable: 'Environment',
      meta: {
        group: 'Refund',
      },
    },
    payoutReason: {
      type: 'string',
      humanReadable: 'Payout Reason',
      meta: {
        group: 'Refund',
      },
      sensitive: true,
    },
    paymentReferenceID: {
      type: 'string',
      humanReadable: 'Payment Reference ID',
      meta: {
        group: 'Refund',
      },
      formattedCell: (value: string) => {
        return h(
          ColumnLink,
          {
            content: value,
            id: value,
            pathName: 'payment',
          },
          () => textWithIconCell(Banknote, value)
        )
      },
    },
    paidWithKID: {
      type: 'string',
      humanReadable: 'Paid With KID',
      meta: {
        group: 'BankDetails',
      },
      sensitive: true,
    },
    fromBankAccountNumber: {
      type: 'string',
      humanReadable: 'From Bank Account Number',
      meta: {
        group: 'BankDetails',
      },
      sensitive: true,
    },
    toBankAccountNumber: {
      type: 'string',
      humanReadable: 'To Bank Account Number',
      meta: {
        group: 'BankDetails',
      },
      sensitive: true,
    },
    amount: {
      type: 'number',
      formatter: MoneyFormatter,
      humanReadable: 'Amount',
      metaType: 'money',
      meta: {
        group: 'Refund',
      },
    },
    shortDescription: {
      type: 'string',
      humanReadable: 'Short Description',
      meta: {
        group: 'Refund',
      },
      sensitive: true,
    },
    sftpPayoutStatus: {
      type: 'enum',
      humanReadable: 'SFTP Payout Status',
      meta: {
        group: 'Refund',
      },
      inSearchSelect: true,
      options: [
        'Pain001IsUploadedToSFTP',
        'Pain002ReceiptIsReceivedFromSFTP',
        'PayoutIsRejected',
        'Pain002PayoutIsAccepted',
        'PayoutIsSuccessful',
      ],
    },
    retryCount: {
      type: 'number',
      humanReadable: 'Retry Count',
      meta: {
        group: 'Refund',
      },
    },
    previouslyRetriedAt: {
      type: 'date',
      humanReadable: 'Previously Retried At',
      meta: {
        group: 'Refund',
      },
    },
    insertTime: {
      type: 'date',
      humanReadable: 'Insert Time',
      meta: {
        group: 'Time',
      },
      isDefaultSortColumn: true,
    },
    updateTime: {
      type: 'date',
      humanReadable: 'Update Time',
      meta: {
        group: 'Time',
      },
    },
    isAccepted: {
      type: 'date',
      humanReadable: 'Refund initiated at',
      meta: {
        group: 'Is Accepted',
        // size: 400,
      },
    },
  },
}

export const RefundsFullSchema: JSSchema<Refunds.Full, typeof RefundGroups> = {
  ...RefundsListSchema,
  title: 'Refund',
}
